<script setup>
import { ref, onMounted } from 'vue'
import PanelOrganic from '@/design/primitives/PanelOrganic.vue'
import ButtonSeed from '@/design/primitives/ButtonSeed.vue'
import CoinGlyph from '@/design/primitives/CoinGlyph.vue'
import { api } from '@/api/client.js'
import { EP } from '@/api/endpoints.js'

const topups  = ref([])
const loading = ref(false)
const error   = ref(null)
let   fetched = false

async function load() {
  if (fetched) return
  loading.value = true
  error.value   = null
  try {
    const res = await api.get(EP.paymentsHistory)
    topups.value = Array.isArray(res.data) ? res.data : []
    fetched = true
  } catch (e) {
    error.value = e.message || 'Could not load top-ups.'
  } finally {
    loading.value = false
  }
}

async function openInvoice(orderId) {
  if (!orderId) return
  try {
    const res = await api.get(`/api/payments/invoice/${encodeURIComponent(orderId)}/pdf`, { responseType: 'blob' })
    const url = URL.createObjectURL(res.data)
    const a = document.createElement('a')
    a.href = url
    a.download = `invoice-${orderId}.pdf`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    setTimeout(() => URL.revokeObjectURL(url), 5000)
  } catch (e) {
    alert(e.response?.data?.message || 'Failed to download invoice')
  }
}

function formatDate(raw) {
  if (!raw) return '—'
  return new Date(raw).toLocaleDateString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric'
  })
}

function statusClass(status) {
  if (!status) return ''
  const s = status.toLowerCase().replace(/\s+/g, '-')
  return `badge--${s}`
}

onMounted(load)
</script>

<template>
  <section class="dossier-section" aria-labelledby="topups-heading">
    <h2 id="topups-heading" class="stamp u-display u-allcaps">Top-ups</h2>

    <PanelOrganic tone="strong" :padded="true">
      <div v-if="loading" class="ledger-state" aria-live="polite">
        <span class="dossier-spinner" aria-hidden="true" />
        <span class="ledger-state__text u-display u-allcaps">Loading top-ups…</span>
      </div>

      <div v-else-if="error" class="ledger-state ledger-state--err" aria-live="assertive">
        <span class="ledger-state__text">{{ error }}</span>
        <ButtonSeed variant="ghost" size="sm" @click="() => { fetched = false; load() }">RETRY</ButtonSeed>
      </div>

      <div v-else-if="!topups.length" class="ledger-state ledger-state--empty">
        <span class="u-display u-allcaps">No top-ups yet</span>
      </div>

      <ul v-else class="ledger" role="list" aria-label="Top-ups">
        <li class="ledger__header" aria-hidden="true">
          <span class="ledger__col ledger__col--date u-display u-allcaps">Date</span>
          <span class="ledger__col ledger__col--order u-display u-allcaps">Order</span>
          <span class="ledger__col ledger__col--credits u-display u-allcaps">EvoGems</span>
          <span class="ledger__col ledger__col--status u-display u-allcaps">Status</span>
          <span class="ledger__col ledger__col--amount u-display u-allcaps">Amount</span>
          <span class="ledger__col ledger__col--actions u-display u-allcaps">&nbsp;</span>
        </li>

        <li
          v-for="t in topups"
          :key="t.orderId"
          class="ledger__row"
        >
          <span class="ledger__col ledger__col--date u-mono">{{ formatDate(t.createdAt) }}</span>
          <span class="ledger__col ledger__col--order u-mono">{{ t.orderId }}</span>
          <span class="ledger__col ledger__col--credits u-mono u-gold">
            <CoinGlyph :size="14" />
            {{ t.credits }}
          </span>
          <span
            class="ledger__col ledger__col--status ledger__badge"
            :class="statusClass(t.status)"
          >
            {{ t.status || 'unknown' }}
          </span>
          <span class="ledger__col ledger__col--amount u-mono">
            {{ (parseFloat(t.amount) || 0).toFixed(2) }} {{ t.currency || 'EUR' }}
          </span>
          <span class="ledger__col ledger__col--actions">
            <button
              v-if="t.status === 'approved'"
              class="btn-invoice"
              @click="openInvoice(t.orderId)"
              title="Open invoice"
            >Invoice</button>
          </span>
        </li>
      </ul>
    </PanelOrganic>
  </section>
</template>

<style scoped>
.dossier-section {
  display: flex;
  flex-direction: column;
  gap: var(--sp-6);
}

.stamp {
  font-size: clamp(var(--fs-21), 2.5vw, var(--fs-32));
  color: var(--gold);
  letter-spacing: 0.12em;
  margin: 0;
}

.ledger {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
}

.ledger__header,
.ledger__row {
  display: grid;
  grid-template-columns: 100px 1fr 90px 90px 110px 90px;
  align-items: center;
  gap: var(--sp-4);
  padding: var(--sp-3) var(--sp-4);
}

.ledger__header {
  border-bottom: 1px solid var(--ash);
  margin-bottom: var(--sp-1);
}

.ledger__header .ledger__col {
  font-size: var(--fs-11, 0.6875rem);
  color: var(--ink-mute);
  letter-spacing: 0.14em;
}

.ledger__row {
  border-bottom: 1px solid var(--ash);
  transition: background var(--t-fast) var(--ease-cinema);
}
.ledger__row:last-child { border-bottom: none; }
.ledger__row:hover { background: rgba(229, 199, 107, 0.04); }

.ledger__col { min-width: 0; font-size: var(--fs-14); color: var(--ink-soft); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ledger__col--status { text-align: center; }
.ledger__col--actions { text-align: center; }

.ledger__col--credits {
  display: flex;
  align-items: center;
  gap: 4px;
}

.ledger__badge {
  display: inline-block;
  padding: 2px 10px;
  border-radius: var(--r-xs);
  font-family: var(--font-display);
  font-size: var(--fs-11, 0.6875rem);
  text-transform: uppercase;
  letter-spacing: 0.1em;
  white-space: nowrap;
  border: 1px solid var(--ash);
  background: var(--bg-deep);
  color: var(--ink-mute);
}
.badge--approved { background: var(--gold-soft); color: var(--gold); border-color: rgba(229, 199, 107, 0.3); }
.badge--pending  { background: rgba(184, 90, 58, 0.12); color: var(--copper); border-color: rgba(184, 90, 58, 0.3); }
.badge--declined,
.badge--error,
.badge--failed   { background: rgba(230, 76, 43, 0.12); color: var(--ember); border-color: rgba(230, 76, 43, 0.3); }

.btn-invoice {
  background: transparent;
  border: 1px solid rgba(229, 199, 107, 0.4);
  color: var(--gold);
  padding: 4px 12px;
  border-radius: var(--r-xs);
  font-family: var(--font-display);
  font-size: var(--fs-11, 0.6875rem);
  letter-spacing: 0.1em;
  text-transform: uppercase;
  cursor: pointer;
  transition: all var(--t-fast) var(--ease-cinema);
}
.btn-invoice:hover {
  background: rgba(229, 199, 107, 0.12);
  border-color: var(--gold);
}

.ledger-state {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--sp-3);
  padding: var(--sp-12) var(--sp-4);
  color: var(--ink-soft);
}
.ledger-state--err   { color: var(--ember); flex-direction: column; }
.ledger-state--empty { color: var(--ink-mute); }
.ledger-state__text  { font-size: var(--fs-14); }

.dossier-spinner {
  display: inline-block;
  width: 24px;
  height: 24px;
  border: 2px solid var(--ash);
  border-top-color: var(--gold);
  border-radius: 50%;
  animation: spin 1.2s linear infinite;
  flex-shrink: 0;
}
@keyframes spin { to { transform: rotate(360deg); } }

@media (max-width: 800px) {
  .ledger__header,
  .ledger__row {
    grid-template-columns: 90px 1fr 90px 100px 90px;
  }
  .ledger__col--credits { display: none; }
}

@media (max-width: 500px) {
  .ledger__header,
  .ledger__row {
    grid-template-columns: 1fr 90px 90px;
  }
  .ledger__col--date,
  .ledger__col--order { display: none; }
}
</style>
